import { useCallback } from 'react';
import { StructureSet } from '~/domain/patient/structureset';
import { Image3D } from '~/domain/patient/image3d';
import { DeformableTransform } from '~/domain/dvf/deformableTransform';
import {
    DeformableContourResolutionStrategy,
    StructureSetCopyService,
} from '~/domain/services/structureSetService/structureSetCopyService';
import { StructureSetObject } from '~/engine/data/structure/StructureSetObject';

export interface WarpStructureSetParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    // Maps each target point to where it lies in the source scan.
    readonly transform: DeformableTransform;
    readonly existingStructureSets: readonly StructureSet[];
    // The ROIs to carry; every ROI when absent.
    readonly selectedRoiNumbers?: ReadonlySet<number>;
}

export function useWarpStructureSet() {
    const warp = useCallback(
        async ({
            sourceStructureSet,
            targetImage,
            transform,
            existingStructureSets,
            selectedRoiNumbers,
        }: WarpStructureSetParams): Promise<StructureSet> => {
            const sourceStructures = new StructureSetObject(sourceStructureSet);
            try {
                const result = await new StructureSetCopyService().copyDeformed(
                    {
                        sourceStructureSet,
                        targetImage,
                        selectedRoiNumbers: new Set(
                            selectedRoiNumbers ??
                                sourceStructureSet.roiArray.map(
                                    (roi) => roi.roiNumber
                                )
                        ),
                        existingStructureSets,
                    },
                    new DeformableContourResolutionStrategy(
                        sourceStructures,
                        transform
                    )
                );
                return result.structureSet;
            } finally {
                sourceStructures.delete();
            }
        },
        []
    );

    return { warp };
}
